/**
 * O perfil e as regras do exame de formalização (E151.F2, GUS-174, ADR-0103 §4.2): o contrato de
 * `GET /api/casos/{id}/formalizacao/perfil`, espelhado à mão dos tipos do servidor e preso pelo teste
 * de paridade (`tests/unit/test_e151_f2_contrato_web.py`), e as regras puras que a tela usa.
 *
 * A seleção é do servidor: a tela não escolhe leitura, não reordena e não deduz admissibilidade.
 */
import axios, { type AxiosInstance } from 'axios';

export type ModoDoExame = 'parte' | 'juizo';
export type ModalidadeDoAto =
  | 'consignado_inss'
  | 'consignado_trabalhador'
  | 'outra'
  | 'indeterminada';
export type ModalidadeDoPerfil = 'consignado_inss' | 'consignado_trabalhador';
export type OrigemDasDatas = 'adotada' | 'hipotese' | 'fuso_presumido';
export type SituacaoDaIncidencia = 'sim' | 'nao' | 'nao_apurada';
export type Incidencia = 'operacao_na_plataforma';
export type FonteDaForma = 'lei' | 'infralegal';
export type EstadoDaRegra = 'preparada' | 'verificada' | 'ativa';
export type CategoriaDoRequisito = 'A' | 'B' | 'C';
export type AtoDoRequisito = 'EV-1' | 'EV-2' | 'EV-3' | 'EV-4';
export type MotivoDeExclusao =
  | 'modo_nao_aplicavel'
  | 'preparada'
  | 'verificada_sem_ativacao'
  | 'posterior_sem_retroacao'
  | 'revogada_ou_encerrada'
  | 'norma_sem_vigencia_conferida'
  | 'norma_suspensa'
  | 'eficacia_condicionada'
  | 'subsidiaria';
export type CodigoDaPendencia =
  | 'sem_data_do_ato'
  | 'data_entre_perfis'
  | 'modalidade_indeterminada'
  | 'modalidade_sem_perfil'
  | 'sem_perfil_na_data'
  | 'sem_leitura_admissivel'
  | 'incidencia_nao_apurada'
  | 'leitura_pertinente_inativa';
export type PapelDoRequisito =
  | 'comum'
  | 'da_leitura'
  | 'procedimento_suprivel'
  | 'procedimento_dispensado'
  | 'inexigivel_no_periodo'
  | 'informativo';

export interface IncidenciaApuradaData {
  incidencia: Incidencia;
  situacao: SituacaoDaIncidencia;
  fonte: string | null;
}

export interface ContextoDoAtoData {
  modo: ModoDoExame;
  modalidade: ModalidadeDoAto;
  datas: string[];
  origem_das_datas: OrigemDasDatas;
  incidencias: IncidenciaApuradaData[];
  produto: string | null;
}

export interface RequisitoSelecionadoData {
  id: string;
  titulo: string;
  categoria: CategoriaDoRequisito;
  fonte_da_forma: FonteDaForma;
  ato: AtoDoRequisito;
  estado: EstadoDaRegra;
  papel: PapelDoRequisito;
  exigivel: boolean;
  incidencia: Incidencia | null;
  situacao_da_incidencia: SituacaoDaIncidencia | null;
  motivo: string | null;
}

export interface LeituraSelecionadaData {
  id: string;
  titulo: string;
  tese: string;
  fonte_da_forma: FonteDaForma;
  fundamento: string;
  prioridade: number;
  subsidiaria: boolean;
  estado: EstadoDaRegra;
  admissivel: boolean;
  motivos: MotivoDeExclusao[];
  requisitos: RequisitoSelecionadoData[];
}

export interface PerfilCandidatoData {
  perfil_id: string;
  titulo: string;
  modalidade: ModalidadeDoPerfil;
  data: string;
  por_leitura_da_data: boolean;
  por_leitura_normativa: boolean;
  inicio_conferido: boolean;
  fim_conferido: boolean;
  leituras: LeituraSelecionadaData[];
  ordem_favoravel: string[];
}

export interface PendenciaData {
  codigo: CodigoDaPendencia;
  detalhe: string;
}

export interface SelecaoDoPerfilData {
  catalogo_versao: string;
  catalogo_sha256: string;
  criterio_de_prioridade: number;
  contexto: ContextoDoAtoData;
  candidatos: PerfilCandidatoData[];
  pendencias: PendenciaData[];
}

export interface ModoDoExameNoCasoData {
  modo: ModoDoExame;
  padrao: boolean;
  definido_em: string | null;
}

export interface PerfilDoExameData {
  case_id: string;
  modo: ModoDoExameNoCasoData;
  metodologia: string;
  aguardando_exame: boolean;
  selecao: SelecaoDoPerfilData;
}

export type MotivoDaFalhaDoPerfil = 'sem_permissao' | 'nao_encontrado' | 'indisponivel';

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

export class PerfilDoExameError extends Error {
  readonly motivo: MotivoDaFalhaDoPerfil;

  constructor(motivo: MotivoDaFalhaDoPerfil) {
    super(`perfil do exame: ${motivo}`);
    this.name = 'PerfilDoExameError';
    this.motivo = motivo;
  }
}

export async function lerPerfilDoExame(
  client: AxiosInstance,
  caseId: string,
): Promise<PerfilDoExameData> {
  try {
    const { data } = await client.get<PerfilDoExameData>(`/casos/${caseId}/formalizacao/perfil`);
    return data;
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (status === HTTP_FORBIDDEN) throw new PerfilDoExameError('sem_permissao');
    if (status === HTTP_NOT_FOUND) throw new PerfilDoExameError('nao_encontrado');
    throw new PerfilDoExameError('indisponivel');
  }
}

/** As leituras que podem decidir o caso, na ordem do servidor: em PARTE, a ordem de prioridade
 * versionada; em JUÍZO, todas as admissíveis, que precisam convergir. */
export function leiturasQueDecidem(candidato: PerfilCandidatoData): LeituraSelecionadaData[] {
  if (candidato.ordem_favoravel.length > 0) {
    const porId = new Map(candidato.leituras.map((l) => [l.id, l]));
    return candidato.ordem_favoravel.flatMap((id) => porId.get(id) ?? []);
  }
  return candidato.leituras.filter((l) => l.admissivel);
}

export function leiturasQueNaoDecidem(candidato: PerfilCandidatoData): LeituraSelecionadaData[] {
  return candidato.leituras.filter((l) => !l.admissivel);
}

export function exigiveis(leitura: LeituraSelecionadaData): RequisitoSelecionadoData[] {
  return leitura.requisitos.filter((r) => r.exigivel);
}

/** Os procedimentos que a forma livre em PARTE torna não decisivos: prova adicional quando
 * cumpridos. */
export function provaAdicional(leitura: LeituraSelecionadaData): RequisitoSelecionadoData[] {
  return leitura.requisitos.filter((r) => r.papel === 'procedimento_dispensado');
}

/** O que a própria norma ainda não exigia na data do ato (implantação, E168.F4), com o motivo. */
export function naoExigidosNaData(leitura: LeituraSelecionadaData): RequisitoSelecionadoData[] {
  return leitura.requisitos.filter((r) => r.papel === 'inexigivel_no_periodo');
}

/** O que a leitura exige mas ainda não está ativo no catálogo: ela não fecha só com isso. */
export function requisitosInativos(leitura: LeituraSelecionadaData): RequisitoSelecionadoData[] {
  return exigiveis(leitura).filter((r) => r.estado !== 'ativa');
}

/** Uma pendência por código: a tela diz o que impede, e as leituras que a causam já aparecem nas
 * regras que não decidem. */
export function pendenciasPorCodigo(pendencias: readonly PendenciaData[]): CodigoDaPendencia[] {
  return [...new Set(pendencias.map((p) => p.codigo))];
}

export function dataBr(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}
